import { addMessage, setPageMessages, setPage } from "../slices/messageSlice"; // adjust path if needed
import { fetchProviderByReference } from "../services/providerService";
import { logErrorEvent } from "../services/logService";

class CommunicationService {
    constructor(fhirClient) {
        if (!fhirClient || typeof fhirClient.request !== "function") {
            throw new Error("Invalid FHIR Client instance!");
        }
        this.client = fhirClient;
    }

    /** Redux-aware wrapper: checks cache before fetching */
    async getCommunicationsPage(
        practitionerId,
        count,
        pageUrl,
        direction,
        store,
        extraSearchParams = "",
        forceRefresh
    ) {
        console.log("📨 getCommunicationsPage()", {
            practitionerId,
            count,
            pageUrl,
            direction,
            extraSearchParams,
            forceRefresh
        });

        const state = store.getState();
        let queryUrl;
        const isBaseQuery = !pageUrl || pageUrl.startsWith("Communication?");

        //
        // ────────────────────────────────────────────────
        // 1. Build base query (inbox/sent/search)
        // ────────────────────────────────────────────────
        //
        if (isBaseQuery) {
            if (pageUrl && pageUrl.startsWith("Communication?")) {
                queryUrl = pageUrl;
            } else {
                const params = new URLSearchParams();
                if (direction === "sent") {
                    params.append("sender", `Practitioner/${practitionerId}`);
                } else {
                    params.append("recipient", `Practitioner/${practitionerId}`);
                }

                params.append("_count", count);
                params.append("_sort", "-sent");

                if (extraSearchParams) {
                    const extraParams = new URLSearchParams(extraSearchParams);
                    for (const [key, value] of extraParams.entries()) {
                        if (key === "sent") {
                            params.append(key, value);
                        } else if (!params.has(key)) {
                            params.append(key, value);
                        }
                    }
                }

                queryUrl = `Communication?${params.toString()}`;
            }
        } else {
            // _getpages URL
            queryUrl = pageUrl;
        }

        console.log("🔍 Fetching communications from:", queryUrl);

        //
        // ────────────────────────────────────────────────
        // 2. Check cache for this exact page URL
        // ────────────────────────────────────────────────
        //
        const cachedPage = !forceRefresh ? state.messages.pageMap?.[queryUrl] : null;
        //console.log("Cahched", cachedPage);
        if (cachedPage) {
            console.log("📦 Returning CACHED page:", queryUrl);
            const messages = cachedPage.ids.map(id => state.messages.byId[id]);

            // determine page number
            const currentPageNumber =
                queryUrl.includes("_getpagesoffset=")
                    ? Math.floor(
                        Number(new URL(queryUrl).searchParams.get("_getpagesoffset")) /
                        count
                    ) + 1
                    : 1;

            store.dispatch(
                setPage({
                    currentPageUrl: queryUrl,
                    nextPageUrl: cachedPage.nextPageUrl || null,
                    prevPageUrl: cachedPage.prevPageUrl || null,
                    total: cachedPage.total ?? cachedPage.ids.length,
                    currentPageNumber
                })
            );

            return {
                data: messages,
                nextPageUrl: cachedPage.nextPageUrl || null,
                prevPageUrl: cachedPage.prevPageUrl || null,
                total: cachedPage.total ?? messages.length
            };
        }

        //
        // ────────────────────────────────────────────────
        // 3. Fetch from FHIR server
        // ────────────────────────────────────────────────
        //
        const result = await this.fetchCommunications(practitionerId, count, queryUrl, direction, store, this.client);
        //console.log("📡 Direction:", direction);
        //console.log("📡 FHIR result:", result);
        //
        // ────────────────────────────────────────────────
        // 4. Cache this page
        // ────────────────────────────────────────────────
        //
        if (result.data && result.data.length > 0) {
            //console.log("Dispatching new messages for viewer", result.data);
            store.dispatch(
                setPageMessages({
                    pageUrl: queryUrl,
                    messages: result.data,
                    nextPageUrl: result.nextPageUrl,
                    prevPageUrl: result.prevPageUrl,
                    total: result.total
                })
            );
        }

        //
        // ────────────────────────────────────────────────
        // 5. Update ActivePage slice
        // ────────────────────────────────────────────────
        //
        const currentPageNumber =
            queryUrl.includes("_getpagesoffset=")
                ? Math.floor(
                    Number(new URL(queryUrl).searchParams.get("_getpagesoffset")) / count
                ) + 1
                : 1;

        store.dispatch(
            setPage({
                currentPageUrl: queryUrl,
                nextPageUrl: result.nextPageUrl || null,
                prevPageUrl: result.prevPageUrl || null,
                total: result.total ?? result.ids.length,
                currentPageNumber
            })
        );

        return result;
    }

    async addReadReceipt(communicationId, practitionerId, store, readDateTime = new Date().toISOString()) {
        try {
            if (!communicationId || !practitionerId) {
                throw new Error("Communication ID and practitioner ID are required!");
            }

            const resource = await this.client.request(`Communication/${communicationId}`);
            const normalizeRef = ref => ref?.replace(/^.*\/Practitioner\//, "Practitioner/").trim();

            const hasExisting = (resource.extension || []).some(ext => {
                if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt") return false;

                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const readAt = ext.extension?.find(e => e.url === "readAt")?.valueDateTime;

                const normalized = normalizeRef(userRef);
                return normalized === practitionerId && !!readAt;
            });

            if (hasExisting) {
                console.log(`🟦 Read receipt already exists for Practitioner/${practitionerId}`);
                return true;
            }

            const patchBody = Array.isArray(resource.extension)
                ? [{
                    op: "add",
                    path: "/extension/-",
                    value: {
                        url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
                        extension: [
                            { url: "user", valueReference: { reference: `Practitioner/${practitionerId}` } },
                            { url: "readAt", valueDateTime: readDateTime }
                        ]
                    }
                }]
                : [{
                    op: "add",
                    path: "/extension",
                    value: [{
                        url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
                        extension: [
                            { url: "user", valueReference: { reference: `Practitioner/${practitionerId}` } },
                            { url: "readAt", valueDateTime: readDateTime }
                        ]
                    }]
                }];

            await fetch(`${this.client.state.serverUrl}/Communication/${communicationId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                    "Authorization": `Bearer ${this.client.state.tokenResponse?.access_token}`
                },
                body: JSON.stringify(patchBody)
            });

            //console.log(dispatch);

            // ✅ Fetch updated resource and dispatch to Redux
            if (store.dispatch) {
                //console.log("fetching updated resource and dispatching to redux...");
                const updated = await this.client.request(`Communication/${communicationId}`);
                store.dispatch(addMessage(updated)); // this will handle update & thread linkage
            }

            console.log(`✅ Read-receipt added and message updated in Redux.`);
            return true;
        } catch (error) {
            console.error("❌ Failed to add read-receipt:", error);
            return false;
        }
    }


    /** Create a new communication resource */
    async createCommunication(communicationData) {
        //console.log("Communication Payload:", JSON.stringify(communicationData, null, 2));

        try {
            if (!communicationData) {
                console.warn("No communication data provided. Skipping request.");
                return null;
            }
            const createdCommunication = await this.client.create({
                resourceType: "Communication",
                ...communicationData
            });

            //console.log("FHIR Server Response:", JSON.stringify(createdCommunication, null, 2));
            return createdCommunication;
        } catch (error) {
            console.error("Error creating communication:", error);
            return null;
        }
    }

    //Get each communication linked to a specific anchor reference and add it to redux.
    async writeConversationIntoRedux(anchorRef, store) {
        //console.log("Loading conversation into reudux for anchorRef", anchorRef);
        if (!anchorRef) {
            console.warn("⚠️ No anchorRef provided");
            return [];
        }

        try {
            const response = await this.client.request(`Communication?part-of=${anchorRef}`);
            const messages = (response.entry || []).map((e) => e.resource);
            //console.log(`📬 Fetched ${messages.length} messages`);

            if (store.dispatch) {
                //console.log("dispatching!");
                await Promise.all(messages.map((msg) => store.dispatch(addMessage(msg))));
            }

            return messages;
        } catch (error) {
            console.error("💥 Failed to write communications into Redux:", error);
            return [];
        }
    }

    /** Fetch communications sent to a practitioner with pagination support */
    async fetchCommunications(practitionerId, count, pageUrl = null, direction = "received", store, fhirClient) {
        console.log("------------------------------Fetching communications:", { practitionerId, count, pageUrl, direction });
        try {
            if (!practitionerId) throw new Error("Practitioner ID is required!");
            if (!fhirClient || !fhirClient.state?.serverUrl) {
                console.warn("FHIR client not ready — skipping fetchCommunications.");
                return { data: [], nextPageUrl: null, prevPageUrl: null, total: 0 };
            }

            let queryUrl = pageUrl
                ? pageUrl.startsWith("http") ? pageUrl : `${fhirClient.state.serverUrl}/${pageUrl}`
                : `${fhirClient.state.serverUrl}/Communication?${direction === "sent" ? `sender=Practitioner/${practitionerId}` : `recipient=Practitioner/${practitionerId}`}&_count=${count}&_sort=-sent`;

            console.log("Fetching communications from:", queryUrl);

            const bundle = await fhirClient.request(queryUrl);
            const nextPageUrl = bundle.link?.find(link => link.relation === "next")?.url || null;
            const prevPageUrl = bundle.link?.find(link => link.relation === "previous")?.url || null;

            const resources = bundle.entry?.map(entry => entry.resource) || [];

            // Collect all unique practitioner references in senders and recipients
            const providerRefs = new Set();
            resources.forEach(comm => {
                if (comm.sender?.reference?.startsWith("Practitioner/")) {
                    providerRefs.add(comm.sender.reference);
                }
                if (Array.isArray(comm.recipient)) {
                    comm.recipient.forEach(r => {
                        if (r.reference?.startsWith("Practitioner/")) {
                            providerRefs.add(r.reference);
                        }
                    });
                }
            });

            // Fetch and register each provider using fetchProviderByReference
            const providerPromises = Array.from(providerRefs).map(ref => fetchProviderByReference(ref, fhirClient, store)
            );

            await Promise.all(providerPromises);

            return { data: resources, nextPageUrl, prevPageUrl, total: bundle.total ?? resources.length ?? 0 };
        } catch (error) {
            console.error("Failed to fetch communications:", error);
            return { data: [], nextPageUrl: null, prevPageUrl: null, total: 0 };
        }
    }




    /** Fetch all communications for a practitioner as a full FHIR Bundle */
    async fetchCommunicationsBundle(practitionerId, count = 10) {
        try {
            if (!practitionerId) {
                throw new Error("Practitioner ID is required!");
            }

            const queryUrl = `Communication?recipient=Practitioner/${practitionerId}&_count=${count}`;
            const bundle = await this.client.request(queryUrl);
            console.log("Fetched Communications (Bundle):", bundle);

            return bundle;
        } catch (error) {
            console.error("Failed to fetch communications bundle:", error);
            return null;
        }
    }

    /** Update a communication's received timestamp */
    /** Update a communication's received timestamp and append read receipt extension */
    async updateReceivedTimestamp(communicationId, receivedDate, practitionerId) {
        try {
            if (!communicationId || !receivedDate || !practitionerId) {
                throw new Error("Communication ID, received date, and practitioner ID are required!");
            }

            const patchBody = [
                {
                    op: "replace",
                    path: "/received",
                    value: receivedDate
                },
                {
                    op: "add",
                    path: "/extension",
                    value: [
                        {
                            url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
                            extension: [
                                {
                                    url: "user",
                                    valueReference: {
                                        reference: `Practitioner/${practitionerId}`
                                    }
                                },
                                {
                                    url: "readAt",
                                    valueDateTime: receivedDate
                                }
                            ]
                        }
                    ]
                }
            ];

            await this.client.request(`Communication/${communicationId}`, {
                method: "PATCH",
                body: JSON.stringify(patchBody),
                headers: { "Content-Type": "application/json-patch+json" }
            });

            console.log(`Communication ${communicationId} marked as received and patched with read receipt.`);
            return true;
        } catch (error) {
            console.error("Failed to update received timestamp and add read-receipt extension:", error);
            return false;
        }
    }

    async sendReply(baseComm, replyText, contextProvider, addAttachments = true) {
        console.log("📤 Sending reply...");
        console.log(baseComm);

        // 🧯 Early exit on missing input
        if (!baseComm || !replyText?.trim()) {
            console.warn("⚠️ Missing base Communication or reply text.");
            return null;
        }

        // 🌐 Reference extraction
        const patientRef = baseComm.subject?.reference;
        const encounterRef = baseComm.encounter?.reference;
        const partOf = baseComm.partOf || [];

        // 👤 Sender is the current practitioner
        const senderRef = contextProvider?.id ? `Practitioner/${contextProvider.id}` : undefined;
        console.log("🧑‍⚕️ Sender:", senderRef);

        // 🎯 Build recipient list, excluding sender
        let recipientRefs = [];

        if (baseComm.sender?.reference && baseComm.sender.reference !== senderRef) {
            recipientRefs.push(baseComm.sender);
        }

        for (const r of baseComm.recipient || []) {
            if (r.reference && r.reference !== senderRef) {
                recipientRefs.push({ reference: r.reference });
            }
        }

        // 🧼 Deduplicate recipients
        const dedupedRecipientRefs = Array.from(
            new Set(recipientRefs.map(r => r.reference))
        ).map(ref => ({ reference: ref }));

        // 🛠 Construct reply payload with full passthrough
        const replyPayload = {
            resourceType: "Communication",
            status: "completed",
            sent: new Date().toISOString(),
            payload: [{ contentString: replyText.trim() }],

            ...(patientRef && { subject: { reference: patientRef } }),
            ...(encounterRef && { encounter: { reference: encounterRef } }),
            ...(senderRef && { sender: { reference: senderRef } }),
            ...(dedupedRecipientRefs.length > 0 && { recipient: dedupedRecipientRefs }),
            ...(partOf.length > 0 && { partOf: structuredClone(partOf) }),

            ...(baseComm.topic && { topic: structuredClone(baseComm.topic) }),
            ...(baseComm.priority && { priority: baseComm.priority }),
            ...(baseComm.category && { category: structuredClone(baseComm.category) }),

            // 🧬 Preserve chaining
            inResponseTo: [
                ...(Array.isArray(baseComm.inResponseTo) ? baseComm.inResponseTo : []),
                ...(baseComm.id ? [{ reference: `Communication/${baseComm.id}` }] : [])
            ]
        };

        // 🔗 Additional passthroughs
        const passthroughFields = [
            "basedOn", "about", "medium", "reasonCode", "reasonReference"
        ];

        for (const field of passthroughFields) {
            if (!baseComm[field]) continue;

            // ⛔ NEW: If attachments are disabled, strip ALL about[] references
            if (field === "about" && !addAttachments) {
                replyPayload.about = [];
                continue;
            }

            replyPayload[field] = Array.isArray(baseComm[field])
                ? [...baseComm[field]]
                : { ...baseComm[field] };
        }

        if (baseComm.note) {
            replyPayload.note = structuredClone(baseComm.note);
        }

        if (baseComm.identifier) {
            replyPayload.identifier = structuredClone(baseComm.identifier);
        }

        if (baseComm.modifierExtension) {
            replyPayload.modifierExtension = structuredClone(baseComm.modifierExtension);
        }

        // 🔄 Clean read receipts and append fresh one
        const extensions = (baseComm.extension || []).filter(ext =>
            ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
        );

        extensions.push({
            url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
            extension: []
        });

        replyPayload.extension = extensions;

        // ⛔ NEW: Remove attachments from payload[]
        if (!addAttachments) {
            replyPayload.payload = replyPayload.payload.map(p => {
                if (p.contentAttachment) {
                    const cleaned = {};
                    if (p.contentString) cleaned.contentString = p.contentString;
                    return cleaned;
                }
                return p;
            });
        }

        // 🚀 Send it off
        try {
            console.log("📦 Reply Payload:", replyPayload);
            return await this.createCommunication(replyPayload);
        } catch (err) {
            console.error("❌ Failed to send reply:", err);
            return null;
        }
    }


    /** Fetch communications using a full FHIR pagination URL */
    async fetchUrl(url) {
        try {
            if (!url) {
                throw new Error("URL is required for paginated fetch.");
            }

            const bundle = await this.client.request(url);

            const nextPageUrl = bundle.link?.find(link => link.relation === "next")?.url || null;
            const prevPageUrl = bundle.link?.find(link => link.relation === "previous")?.url || null;

            return {
                data: bundle.entry?.map(entry => entry.resource) || [],
                nextPageUrl,
                prevPageUrl,
                total: bundle.total ?? bundle.entry?.length ?? 0
            };
        } catch (error) {
            console.error("❌ Failed to fetch paginated URL:", error);
            return { data: [], nextPageUrl: null, prevPageUrl: null, total: 0 };
        }
    }


    async addStatusFlag(communicationId, flagType, practitionerId, store) {
        console.log("Adding status flag:", flagType, "for Practitioner:", practitionerId);
        try {
            if (!communicationId || !flagType || !practitionerId) {
                throw new Error("Communication ID, flag type, and practitioner ID are required!");
            }

            const resource = await this.client.request(`Communication/${communicationId}`);

            const hasExisting = (resource.extension || []).some(ext => {
                if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const normalizedRef = userRef?.split("/")?.pop();
                const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                return normalizedRef === practitionerId && flagCode === flagType;
            });

            if (hasExisting) {
                console.log(`🟦 Status flag '${flagType}' already exists for Practitioner/${practitionerId}`);
                return true;
            }

            const newFlagExtension = {
                url: "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag",
                extension: [
                    { url: "user", valueReference: { reference: `Practitioner/${practitionerId}` } },
                    { url: "flag", valueCode: flagType }
                ]
            };

            const patchBody = Array.isArray(resource.extension)
                ? [{ op: "add", path: "/extension/-", value: newFlagExtension }]
                : [{ op: "add", path: "/extension", value: [newFlagExtension] }];

            await fetch(`${this.client.state.serverUrl}/Communication/${communicationId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                    "Authorization": `Bearer ${this.client.state.tokenResponse?.access_token}`
                },
                body: JSON.stringify(patchBody)
            });

            if (store.dispatch) {
                const updated = await this.client.request(`Communication/${communicationId}`);
                store.dispatch(addMessage(updated));
            }

            console.log(`✅ Status flag '${flagType}' set for Practitioner/${practitionerId}`);
            return true;
        } catch (error) {
            console.error("❌ Failed to set status flag:", error);
            return false;
        }
    }

    async removeStatusFlag(communicationId, flagType, practitionerId, dispatch) {
        console.log("Removing status flag");
        try {
            const resource = await this.client.request(`Communication/${communicationId}`);
            const originalExtensions = resource.extension || [];

            const filtered = originalExtensions.filter(ext => {
                if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return true;
                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const normalizedRef = userRef?.split("/")?.pop();
                const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                return !(normalizedRef === practitionerId && flagCode === flagType);
            });

            const patchBody = [{ op: "replace", path: "/extension", value: filtered }];

            await fetch(`${this.client.state.serverUrl}/Communication/${communicationId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                    "Authorization": `Bearer ${this.client.state.tokenResponse?.access_token}`
                },
                body: JSON.stringify(patchBody)
            });

            if (dispatch) {
                const updated = await this.client.request(`Communication/${communicationId}`);
                dispatch(addMessage(updated));
            }

            console.log(`✅ Removed '${flagType}' for Practitioner/${practitionerId}`);
            return true;
        } catch (error) {
            console.error("❌ Failed to remove status flag:", error);
            return false;
        }
    }

    /**
     * Fetches a Communication by ID with optional cache bypass
     */
    async fetchCommunication(communicationId, store, { forceFetch = false } = {}, isAnchor) {
        if (!communicationId) return null;

        const cached = store.getState().messages?.[communicationId];
        if (cached && !forceFetch) return cached;

        try {
            const fetched = await this.client.request(`Communication/${communicationId}`);
            if (!isAnchor)
                store.dispatch(addMessage(fetched));
            return fetched;

        } catch (err) {
            console.error(`❌ Failed to fetch Communication/${communicationId}:`, err);
            const status = err?.response?.status || err?.status || 500;
            logErrorEvent({
                errorType: "read",
                error: {
                    message: err?.message || "Communication fetch failed",
                    status,
                    resourceId: `Communication/${communicationId}`
                },
                description: `Failed to retrieve Communication/${communicationId} from FHIR server`,
                store
            });

            return { error: status };
        }
    }

    /**
     * Fetches a Communication by reference (e.g., "Communication/abc123")
     */
    async fetchCommunicationByReference(ref, dispatch, store, options = {}, isAnchor) {
        if (!ref || !ref.startsWith("Communication/") || ref === "Communication/undefined") {
            console.warn("⚠️ Invalid Communication reference:", ref);
            return null;
        }

        const communicationId = ref.split("/")[1];
        if (!communicationId) {
            console.warn("⚠️ Could not extract Communication ID from reference:", ref);
            return null;
        }

        return this.fetchCommunication(communicationId, dispatch, store, options, isAnchor);
    }



}

export default CommunicationService;